// Dátumové pomocné funkcie (Local Date)
// Zabezpečujú správne formátovanie bez nechcených posunov časových pásiem (UTC vs Local)

/**
 * Bezpečne naformátuje dátum na 'YYYY-MM-DD' v lokálnom čase bez posunov časového pásma.
 * Akceptuje Date objekt, reťazec (aj 'YYYY-MM-DD' alebo ISO string) alebo timestamp.
 * @param {Date|string|number} dateVal 
 * @returns {string}
 */
export function formatDateForBackend(dateVal) {
  if (!dateVal) return "";
  if (typeof dateVal === "string") {
    const trimmed = dateVal.trim();
    if (trimmed.length >= 10 && /^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
      return trimmed.slice(0, 10);
    }
  }
  const d = new Date(dateVal);
  if (isNaN(d.getTime())) return "";
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Vráti dnešný kalendárny dátum ako string 'YYYY-MM-DD' v slovenskom časovom pásme (Europe/Bratislava).
 * Zaručuje, že aj keď používateľ otvorí web napr. z USA alebo Ázie, dnešok bude podľa slovenského času.
 * @param {string} timeZone
 * @returns {string}
 */
export function getTodayDateString(timeZone = "Europe/Bratislava") {
  try {
    return new Intl.DateTimeFormat("en-CA", { timeZone }).format(new Date());
  } catch {
    return formatDateForBackend(new Date());
  }
}

/**
 * Bezpečne naformátuje dátum do slovenského formátu 'DD.MM.YYYY'.
 * @param {Date|string|number} dateVal 
 * @returns {string}
 */
export function formatDateSk(dateVal) {
  if (!dateVal) return "";
  if (typeof dateVal === "string") {
    const match = dateVal.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const [, y, m, d] = match;
      return `${parseInt(d, 10)}.${parseInt(m, 10)}.${y}`;
    }
  }
  const d = new Date(dateVal);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("sk-SK");
}

/**
 * Bezpečne naformátuje dátum a čas do slovenského formátu 'DD.MM.YYYY HH:mm'.
 * @param {Date|string|number} dateVal 
 * @returns {string}
 */
export function formatDateTimeSk(dateVal) {
  if (!dateVal) return "";
  if (typeof dateVal === "string") {
    const match = dateVal.trim().match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2}))?/);
    if (match) {
      const [, y, m, d, hh, mm] = match;
      const datePart = `${parseInt(d, 10)}.${parseInt(m, 10)}.${y}`;
      return hh !== undefined ? `${datePart} ${hh}:${mm}` : datePart;
    }
  }
  const d = new Date(dateVal);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleString("sk-SK", {
    day: "numeric",
    month: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}


/**
 * Bezpečne pripočíta počet mesiacov k dátumu a vráti 'YYYY-MM-DD' v lokálnom čase.
 * @param {Date|string} dateFrom 
 * @param {number} months 
 * @returns {string}
 */
export function addMonthsToDate(dateFrom, months = 3) {
  if (!dateFrom) return "";
  let baseDate;
  if (typeof dateFrom === "string") {
    const match = dateFrom.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const [, y, m, d] = match;
      baseDate = new Date(Number(y), Number(m) - 1, Number(d));
    }
  }
  if (!baseDate) {
    baseDate = new Date(dateFrom);
  }
  if (isNaN(baseDate.getTime())) return "";
  baseDate.setMonth(baseDate.getMonth() + months);
  return formatDateForBackend(baseDate);
}
